/**
 * Logger utility with configurable log levels
 * 
 * Usage:
 *   import { logger } from '../utils/logger'
 *   logger.debug('Verbose debug info')
 *   logger.info('Important state changes')
 *   logger.warn('Warnings and recoverable issues')
 *   logger.error('Errors and exceptions')
 * 
 * Runtime control (in browser console):
 *   setLogLevel('DEBUG')  // Show all logs
 *   setLogLevel('INFO')   // Show info, warn, error (default)
 *   setLogLevel('WARN')   // Show warn, error only
 *   setLogLevel('ERROR')  // Show errors only
 *   setLogLevel('NONE')   // Silence all logs
 * 
 * Setting persists in localStorage across sessions
 */

export const LogLevel = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  NONE: 4
} as const

type LogLevelKey = keyof typeof LogLevel
type LogLevelValue = typeof LogLevel[LogLevelKey]

class Logger {
  private level: LogLevelValue

  constructor() {
    // Check localStorage first (if available), then fall back to INFO
    let stored: string | null = null
    try {
      if (typeof localStorage !== 'undefined') {
        stored = localStorage.getItem('LOG_LEVEL')
      }
    } catch {
      // Ignore security errors or missing localStorage
    }

    const levelKey = stored as LogLevelKey | null
    if (levelKey && levelKey in LogLevel) {
      this.level = LogLevel[levelKey]
    } else {
      this.level = LogLevel.INFO
    }
  }

  setLevel(level: LogLevelValue) {
    this.level = level
    const levelName = Object.keys(LogLevel).find(k => LogLevel[k as LogLevelKey] === level) as LogLevelKey
    localStorage.setItem('LOG_LEVEL', levelName)
  }

  debug(...args: any[]) {
    if (this.level <= LogLevel.DEBUG) {
      console.log(...args)
    }
  }

  info(...args: any[]) {
    if (this.level <= LogLevel.INFO) {
      console.log(...args)
    }
  }

  warn(...args: any[]) {
    if (this.level <= LogLevel.WARN) {
      console.warn(...args)
    }
  }

  error(...args: any[]) {
    if (this.level <= LogLevel.ERROR) {
      console.error(...args)
    }
  }
}

export const logger = new Logger()

// Expose to window for easy debugging
if (typeof window !== 'undefined') {
  (window as any).setLogLevel = (level: LogLevelKey) => {
    logger.setLevel(LogLevel[level])
    console.log(`Log level set to ${level}`)
  }
}
